import { isElementFocusable, slug } from "@resamare/functions";
import { RefObject, useEffect, useMemo } from "react";
import {
    FieldValues,
    FormProvider,
    SubmitErrorHandler,
    SubmitHandler,
    useFormContext,
} from "react-hook-form";
import { ObjectSchema } from "yup";
import { Divider } from "../../General";
import { Stack } from "../../Layout";
import { Form } from "../Form/Form";
import { useForm } from "../Form/useForm";
import { SECTION_PREFIX } from "./config";
import { FormBuilderSection } from "./FormBuilderSection";
import { FormData } from "./types";
import { buildYupSchema } from "./validation/yup-builder";

type FormBuilderProps<FormValues extends FieldValues> = {
    children?: React.ReactNode;
    data: FormData;
    formRef?: RefObject<HTMLFormElement>;
    onMount?: (schema: ObjectSchema<FormValues>) => void;
    onSubmit?: SubmitHandler<FormValues>;
    showFullSectionDescription?: boolean;
};

export function FormBuilder<FormValues extends FieldValues>({
    children,
    data,
    formRef,
    onMount,
    onSubmit,
    showFullSectionDescription = false,
}: FormBuilderProps<FormValues>) {
    const schema = useMemo(() => buildYupSchema<FormValues>(data), [data]);
    const methods = useForm({ schema });
    const contextMethods = useFormContext<FormValues>();

    const fields = data.sections.map((section, index) => (
        <Stack gap={5} key={slug(`${SECTION_PREFIX}-${section.id}`)}>
            <FormBuilderSection
                section={section}
                showFullSectionDescription={showFullSectionDescription}
            />
            {index !== data.sections.length - 1 ? <Divider /> : null}
        </Stack>
    ));

    useEffect(() => {
        if (onMount) {
            onMount(schema);
        }
    }, [schema]);

    if (contextMethods) {
        return (
            <FormBuilderComponent formRef={formRef} onSubmit={onSubmit}>
                {children}
                {fields}
            </FormBuilderComponent>
        );
    }

    return (
        <FormProvider {...methods}>
            <FormBuilderComponent formRef={formRef} onSubmit={onSubmit}>
                {children}
                {fields}
            </FormBuilderComponent>
        </FormProvider>
    );
}

interface FormBuilderComponentProps<FormValues extends FieldValues> {
    children: React.ReactNode;
    formRef?: RefObject<HTMLFormElement>;
    onSubmit?: SubmitHandler<FormValues>;
}

function FormBuilderComponent<FormValues extends FieldValues>({
    children,
    formRef,
    onSubmit,
}: FormBuilderComponentProps<FormValues>) {
    const handleOnErrors: SubmitErrorHandler<FormValues> = (errors) => {
        const element = document.querySelector<HTMLInputElement>(`#${Object.keys(errors)[0]}`);
        element?.scrollIntoView({ block: "center", behavior: "smooth" });
        if (element && isElementFocusable(element)) {
            element.focus();
        }
    };

    return (
        <Form formRef={formRef} onError={handleOnErrors} onSubmit={onSubmit}>
            <Stack gap={5}>{children}</Stack>
        </Form>
    );
}
